/**
 * Per-agent integration allowlist (least-privilege) — shared frontend helpers.
 *
 * Given an integration-status payload (the shape returned by both
 * GET /marketplace/workflows/{slug}/integration-status and
 * GET /workflows/{uuid}/integrations/status):
 *
 *   { required, connected, missing, optional, optionalConnected,
 *     optionalMissing, integrations }
 *
 * where each list entry is a provider-id string OR an `{ any: [...] }` OR-group
 * (a CONNECTED OR-group additionally carries `connectedMembers: string[]`).
 *
 * These helpers derive, from that payload:
 *   - connectedProviders(status)  → the flat set of provider ids that are
 *     actually connected (so they can be offered for selection — only connected
 *     integrations make sense in the allowlist; you can't enable a tool whose
 *     integration isn't connected).
 *   - requiredConnectedProviders(status) → the subset of the above that the
 *     workflow REQUIRES (members of a required string / required OR-group that
 *     are connected). These must be locked-ON in the UI and force-included in
 *     the payload — deselecting a required integration would break the agent.
 *
 * Both return Sets. Defensive against missing/partial payloads.
 */

// Pull the flat connected provider ids out of one "connected" list (required's
// `connected` or optional's `optionalConnected`). String entries are connected
// as-is; OR-group entries contribute their `connectedMembers`.
function flattenConnected(list) {
  const out = new Set();
  for (const entry of Array.isArray(list) ? list : []) {
    if (typeof entry === 'string') {
      out.add(entry);
    } else if (entry && Array.isArray(entry.connectedMembers)) {
      for (const m of entry.connectedMembers) if (typeof m === 'string') out.add(m);
    }
  }
  return out;
}

/**
 * The no-connection toggleable skills the status feed declares (e.g.
 * codebase-memory) as a flat array of `{ id, name, description, enabled }`. These
 * reuse the SAME allowlist as integration providers but have NO Connect step —
 * they're always "candidates" (no connection gate). Defensive against absent /
 * malformed payloads.
 * @param {object|null} status
 * @returns {Array<{id:string,name?:string,description?:string,enabled?:boolean}>}
 */
export function toggleableSkills(status) {
  if (!status || typeof status !== 'object' || !Array.isArray(status.toggleableSkills)) return [];
  return status.toggleableSkills.filter((s) => s && typeof s.id === 'string');
}

/**
 * All provider ids that are currently connected for this workflow's account —
 * across BOTH the required and optional sections — PLUS every no-connection
 * toggleable skill id (codebase-memory etc). These are the candidates the user
 * can include/exclude in the allowlist. Toggleable skills are always candidates
 * (no connection required), so they're members of this set whenever the workflow
 * declares them.
 * @param {object|null} status
 * @returns {Set<string>}
 */
export function connectedProviders(status) {
  const out = new Set();
  if (!status || typeof status !== 'object') return out;
  for (const id of flattenConnected(status.connected)) out.add(id);
  for (const id of flattenConnected(status.optionalConnected)) out.add(id);
  for (const s of toggleableSkills(status)) out.add(s.id);
  return out;
}

/**
 * The connected provider ids that the workflow REQUIRES (and so must be
 * locked-ON + force-included). Derived from the REQUIRED section's `connected`
 * list only — optional integrations are never forced.
 * @param {object|null} status
 * @returns {Set<string>}
 */
export function requiredConnectedProviders(status) {
  if (!status || typeof status !== 'object') return new Set();
  return flattenConnected(status.connected);
}

/**
 * Compute the initial allowlist selection for a workflow.
 *
 * - When the workflow has NO stored allowlist (`enabledIntegrations` absent /
 *   null) → default ALL-ON: every connected provider AND every no-connection
 *   toggleable skill (codebase-memory etc) is selected (the backward-compatible
 *   "all on" default, now made explicit — toggleable skills default ON too).
 * - When it HAS a stored allowlist → start from that, intersected with what's
 *   actually connected (a provider may have been disconnected since).
 * - In BOTH cases the required-connected providers are force-included (locked).
 *
 * @param {object|null} status               the integration-status payload
 * @param {string[]|null|undefined} stored    the workflow row's enabledIntegrations
 * @returns {Set<string>} the selected provider ids
 */
export function initialSelection(status, stored) {
  const connected = connectedProviders(status);
  const required = requiredConnectedProviders(status);
  let selected;
  if (Array.isArray(stored)) {
    selected = new Set(stored.filter((id) => connected.has(id)));
  } else {
    selected = new Set(connected); // default all-on
  }
  for (const id of required) selected.add(id); // force-in (locked)
  return selected;
}
